import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLibrary } from '../context/LibraryContext';
import { useBooks } from '../context/BooksContext';
import { IoBook, IoTime, IoHeart, IoTrophy } from 'react-icons/io5';
import { estimateBookReadingTime } from '../utils/readingTime';
import './ReadingStats.css';

export default function ReadingStats() {
  const { library, getProgress } = useLibrary();
  const { books, getBookById } = useBooks();

  const stats = useMemo(() => {
    let totalMinutes = 0;
    let booksFinished = 0;
    let genres = {};

    library.forEach(book => {
      const fullBook = getBookById(book.id);
      if (!fullBook) return;

      const progress = getProgress(book.id);
      const totalChapters = fullBook.chapters.length;

      // Temps lu (approximation basée sur la progression)
      const bookTime = estimateBookReadingTime(fullBook.chapters);
      const readPercent = totalChapters > 0 ? (progress + 1) / totalChapters : 0;
      totalMinutes += Math.round(bookTime * readPercent);

      // Livres terminés
      if (progress >= totalChapters - 1 && totalChapters > 0) booksFinished++;

      // Genres
      if (book.genre) {
        genres[book.genre] = (genres[book.genre] || 0) + 1;
      }
    });

    const topGenre = Object.entries(genres).sort((a, b) => b[1] - a[1])[0];

    return {
      totalMinutes,
      booksFinished,
      booksInLibrary: library.length,
      topGenre: topGenre ? topGenre[0] : null,
      genreCount: Object.keys(genres).length,
    };
  }, [library, books, getProgress, getBookById]);

  const formatTime = (min) => {
    if (min < 60) return min + ' min';
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h + 'h' + (m > 0 ? m + 'min' : '');
  };

  const genreLabels = { romance: '💕 Romance', fantasy: '🗡️ Fantasy', thriller: '🔍 Thriller', scifi: '🚀 Sci-Fi', drama: '🎭 Drame', horror: '👻 Horreur', humor: '😂 Humour', poetry: '✨ Poésie' };

  return (
    <div className="rstats-section">
      <h3 className="rstats-title">📊 Mes stats de lecture</h3>
      <div className="rstats-grid">
        <motion.div className="rstats-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <IoTime className="rstats-icon" style={{ color: 'var(--amber)' }} />
          <span className="rstats-value">{formatTime(stats.totalMinutes)}</span>
          <span className="rstats-label">Temps de lecture</span>
        </motion.div>
        <motion.div className="rstats-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <IoTrophy className="rstats-icon" style={{ color: 'var(--sage)' }} />
          <span className="rstats-value">{stats.booksFinished}</span>
          <span className="rstats-label">Livres terminés</span>
        </motion.div>
        <motion.div className="rstats-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <IoBook className="rstats-icon" style={{ color: 'var(--sky)' }} />
          <span className="rstats-value">{stats.booksInLibrary}</span>
          <span className="rstats-label">En bibliothèque</span>
        </motion.div>
        <motion.div className="rstats-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <IoHeart className="rstats-icon" style={{ color: 'var(--rose)' }} />
          <span className="rstats-value">{stats.topGenre ? genreLabels[stats.topGenre] || stats.topGenre : '—'}</span>
          <span className="rstats-label">Genre préféré</span>
        </motion.div>
      </div>
    </div>
  );
}